import React, { useRef, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { Icon } from './Icon';
import UploadIndicator from './UploadIndicator';
import type { UploadProgress } from '../lib/upload/retry';

interface Props {
  /** Translator. Tenant passes `useLocale().t`; admin passes a Spanish-fallback
   *  resolver (admin has no i18n runtime). Keys: catalog.image*. */
  t: (key: string) => string;
  /** Existing image URL (after a successful upload, or from the master row). */
  imageUrl?: string | null;
  /** Read-only mode: show preview if any, hide upload/remove controls. */
  readOnly?: boolean;
  /**
   * For "create" flows we have no master id yet — the form holds the File in
   * local state and uploads it after the master is created. The picker tells
   * the parent which file the user picked and (optionally) an object URL for
   * preview.
   */
  pendingFile?: File | null;
  onPick?: (file: File | null) => void;
  /**
   * For "edit" flows we already have an id — the picker uploads
   * directly via the parent-supplied callback and re-renders with the new url.
   */
  onUpload?: (file: File) => Promise<void>;
  /** Optional callback to clear the existing image. */
  onClear?: () => Promise<void> | void;
  className?: string;
  label?: string;
  /** Render a square that fills the parent column's WIDTH (instead of the
   *  default 180px-capped square). Used by the catalog identity layout: the
   *  parent (`MaterialForm`) sets the column width to track how many field rows
   *  sit beside it (compact for a 2-row manufacturer, wider for a 3-row
   *  material), so the square is never taller than those fields. Always square
   *  (`aspect-square`), desktop and mobile. */
  fill?: boolean;
  /** Compact 64px tile (icon-only, no hint text) for dense grids such as the
   *  product images modal, where it must match the existing thumbnails. The
   *  default is the full 180px square used by the catalog forms. */
  size?: 'sm' | 'md';
  /** When set, the empty picker is non-interactive and shows this text as a
   *  tooltip (e.g. the cover-photo cap is full). Filled-image controls are
   *  unaffected. */
  disabledReason?: string;
  /** Pending mode: the progress of the picked image while the parent's Save
   *  uploads it (`useUploadProgress`) — a ring in the corner of the preview. */
  upload?: UploadProgress;
}

const ACCEPTED = 'image/png, image/jpeg, image/webp, image/gif';
const MAX_BYTES = 25 * 1024 * 1024;

/**
 * Single-image picker with preview + drag-drop + click-to-browse. Shared by
 * tenant + platform-admin catalog forms. Two flows:
 *
 * - **Pending mode** (``onPick`` supplied): the picked file lives in the
 *   parent's state and is uploaded after a parent action (e.g. create form).
 *   Preview is rendered from a temporary object URL.
 * - **Direct upload mode** (``onUpload`` supplied): the picker uploads
 *   immediately and the parent updates the master's ``image_url``.
 *
 * Read-only mode shows the existing image (or a placeholder) without controls.
 * i18n via the ``t`` prop so the component carries no app coupling.
 */
export default function SingleImagePicker({
  t,
  imageUrl,
  readOnly = false,
  pendingFile = null,
  onPick,
  onUpload,
  onClear,
  className = '',
  label,
  fill = false,
  size = 'md',
  disabledReason,
  upload,
}: Props) {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  // Build preview URL from either the pending file (object URL) or the
  // server-side image_url.
  React.useEffect(() => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      setPendingUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPendingUrl(null);
  }, [pendingFile]);

  const previewSrc = pendingUrl || imageUrl || null;
  const hasImage = !!previewSrc;
  const sm = size === 'sm';
  const disabled = !!disabledReason;

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED.split(',').map((s) => s.trim()).includes(file.type)) {
      return t('catalog.imageInvalidType');
    }
    if (file.size > MAX_BYTES) {
      return t('catalog.imageTooLarge');
    }
    return null;
  };

  const handleFile = async (file: File) => {
    const err = validateFile(file);
    if (err) {
      toast.error(err);
      return;
    }
    if (onPick) {
      onPick(file);
      return;
    }
    if (onUpload) {
      setUploading(true);
      try {
        await onUpload(file);
      } finally {
        setUploading(false);
      }
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly || disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = '';
  };

  return (
    <div className={`${sm ? 'w-16 shrink-0' : 'w-full'} ${fill ? 'flex flex-col' : ''} ${className}`}>
      {label && (
        <span className="block text-[10px] font-bold text-primary uppercase tracking-wider mb-1">
          {label}
        </span>
      )}

      <div
        className={`relative border-2 border-dashed transition-colors overflow-hidden
          ${sm ? 'aspect-square w-16 rounded-md' : `rounded-xl ${fill ? 'aspect-square w-full' : 'aspect-square w-full max-w-[180px]'}`}
          ${dragOver
            ? 'border-primary bg-primary/5'
            : 'border-border-subtle dark:border-border-subtle-dark bg-slate-50/50 dark:bg-slate-800/30'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : (readOnly || hasImage ? '' : 'cursor-pointer')} group`}
        title={disabled ? disabledReason : undefined}
        onClick={() => { if (!readOnly && !disabled && !uploading && !hasImage) inputRef.current?.click(); }}
        onDragOver={(e) => {
          if (readOnly || disabled) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        {hasImage ? (
          <>
            <img
              src={previewSrc!}
              alt={t('catalog.imagePreviewAlt')}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {!readOnly && (
              // Centred action overlay — same visual language as PhotoThumb:
              // dark veil on hover, white icons (pencil = replace, trash = remove).
              <div className={`absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center ${sm ? 'gap-1.5' : 'gap-4'}`}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                  className="text-white hover:text-primary transition-colors disabled:opacity-50"
                  aria-label={t('catalog.imageReplace')}
                  title={t('catalog.imageReplace')}
                  disabled={uploading}
                >
                  <Icon name="edit" className={`${sm ? '!text-[16px]' : '!text-[24px]'}`} aria-hidden />
                </button>
                {onClear && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await onClear();
                    }}
                    className="text-white hover:text-red-400 transition-colors disabled:opacity-50"
                    aria-label={t('catalog.imageRemove')}
                    title={t('catalog.imageRemove')}
                    disabled={uploading}
                  >
                    <Icon name="delete" className={`${sm ? '!text-[16px]' : '!text-[24px]'}`} aria-hidden />
                  </button>
                )}
              </div>
            )}
            {upload && (
              <span className={`absolute ${sm ? 'bottom-0.5 right-0.5 h-[22px] w-[22px]' : 'bottom-1.5 right-1.5 h-[32px] w-[32px]'} flex items-center justify-center rounded-full bg-white/90 dark:bg-surface-dark/90 shadow-sm`}>
                <UploadIndicator progress={upload} size={sm ? 'sm' : 'md'} />
              </span>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Icon name="progress_activity" className={`animate-spin text-white ${sm ? '!text-[18px]' : '!text-[28px]'}`} aria-hidden />
              </div>
            )}
          </>
        ) : (
          <div className={`absolute inset-0 flex flex-col items-center justify-center text-center ${sm ? 'p-1' : 'gap-2 p-3'}`}>
            <Icon name={disabled ? 'block' : 'add_photo_alternate'} className={`${sm ? '!text-[22px]' : '!text-[36px]'} text-slate-300 dark:text-slate-600`} aria-hidden />
            {!sm && (
              <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500">
                {readOnly ? t('catalog.imageNone') : t('catalog.imageDropHint')}
              </span>
            )}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={onChange}
        disabled={readOnly || uploading}
      />
    </div>
  );
}
